import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ColDef, ValueGetterParams } from '@ag-grid-community/core';
import {
  ConfigurationId,
  EDSPaginationPageChangedEvent,
  PaginationActionEnum,
  PortfolioDisplay,
  PortfolioDownloadableTableRowData,
  PortfolioMoneyFundDownloadableTableRowData,
} from '@types';
import {
  AgGridService,
  AnalyticsService,
  DropdownItem,
  HeaderRendererComponent,
  InfoItem,
  PagedRecords,
  PaginationComponent,
  ResponsiveService,
} from '@frk/eds-components';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import {
  PortfolioHolding,
  PortfolioTopHoldingTable,
  PortfolioTopTenHoldingMoneyFunds,
} from '@models';
import { AgGridAngular } from '@ag-grid-community/angular';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { Logger } from '@utils/logger';
import { TranslateService } from '@shared/translate/translate.service';
import { floatComparator, stringCompare } from '@utils/sort-utils';
import { formatPercentage } from '@utils/text/number-utils';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { SiteConfigService } from '@services/site-config.service';
import { ANALTICS_PAGINATION_EVENT } from '@utils/app.constants';
import { Title } from '@angular/platform-browser';
import { DebugService } from './../../../../services/debug.service';

const logger = Logger.getLogger('PortfolioHoldingsComponent');

export interface PortfolioHoldingData {
  fundName: string;
  title: string;
  caveatPlacement?: string;
  secondaryCaveatPlacement?: string;
  proximalTop?: string;
  secondaryProximalTop?: string;
  proximalBottom?: string;
  secondaryProximalBottom?: string;
  tooltip?: string;
  asOfLabel?: string;
  asOfDate?: string;
  calculationBasis?: string;
  calculationType?: string;
  updateFrequency?: string;
  productType: ConfigurationId;
  holdings:
    | PortfolioHolding[]
    | PortfolioTopTenHoldingMoneyFunds[]
    | PortfolioTopHoldingTable[];
  hideColumns?: string[];
  isTopHoldingData?: boolean;
  benchmarkName?: string;
  isTopTenHoldingsMoneyFunds?: boolean;
  filteredAsOfDate?: DropdownItem[];
}

@Component({
  selector: 'ft-portfolio-holdings',
  templateUrl: './portfolio-holdings.component.html',
  styleUrls: ['./portfolio-holdings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioHoldingsComponent
  extends BaseProductDetailComponent
  implements PortfolioDisplay, OnInit, OnDestroy {
  @Input() data: PortfolioHoldingData;
  public isPopulated = false;
  totalPages = 0;
  paginatedRecords: PortfolioHolding[] = [];
  protected pagedRecords: PagedRecords<PortfolioHolding>;
  dataSubscription: Subscription[] = [];
  holdingsColumnSearchList: string[] = [];
  holdingsColumnFilteredList: PortfolioHolding[] = [];
  showPagination = true;
  isHandheld$: Observable<boolean>;
  currentPage: PortfolioHolding[] = [];
  selectedFilteredDate: string;

  // *** AG-Grid related declarations ***
  gridContext = {
    filteredRows: null,
  };
  frameworkComponents = {};
  defaultColDef: ColDef = {
    sortable: true,
    cellStyle: {
      'word-break': 'break-word',
    },
    headerComponentParams: {
      theme: 'dark',
    },
  };
  columnDefs: ColDef[] = [];
  sortedTableRows: PortfolioHolding[] = [];

  @ViewChild('holdingsGrid', { static: false, read: AgGridAngular })
  holdingsGrid: AgGridAngular;
  @ViewChild('holdingsGrid', { static: false, read: ElementRef })
  holdingsEl: ElementRef;
  @ViewChild(PaginationComponent, { static: false })
  paginationComponent: PaginationComponent;

  private isProcessingFilter = false;
  showMoneyfund = false;
  /**
   * Event Id used in edsButton directive
   */
  eventId = 'file_download';

  /**
   * Custom parameters to be used in edsButton directive
   */
  eventParams = {};

  private unsubscribe$: Subject<void> = new Subject<void>();

  /**
   * default pagesize for the tables
   */
  pageSize = 20;

  constructor(
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private responsiveService: ResponsiveService,
    private caveatService: CaveatService,
    private siteConfigService: SiteConfigService,
    private analytics: AnalyticsService,
    private titleService: Title,
    private debugService: DebugService,
    private agGridService: AgGridService
  ) {
    super();

    this.pagedRecords = new PagedRecords<PortfolioHolding>(this.pageSize); // Page size set to '20', ref. story.
  }

  ngOnInit() {
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.createEventParams(this.data);

    this.debugService
      .getExpandAllComponents$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expandAll: boolean) => {
        if (expandAll) {
          this.pageSize = this.pagedRecords.expandAll();
        }
      });
  }

  public onGridReady(): void {
    this.agGridService.registerGrid(this.holdingsGrid, this.holdingsEl);
    if (this.data?.filteredAsOfDate?.length > 1) {
      const selectedItem = this.data?.filteredAsOfDate[0];
      this.filterOnDate(selectedItem);
    } else {
      this.selectedFilteredDate = this.data?.filteredAsOfDate?.length
        ? this.data?.filteredAsOfDate[0]?.label
        : '';
    }
  }

  /**
   * When data is passed into this component, this function checks if it can be displayed
   * Called direct from Entry component, as ngOnInit and ngOnchanges not called when dynamic component
   */
  public processData(): void {
    logger.debug('processData()', this.data);
    this.isProcessingFilter = false;

    if (this.data && this.data.holdings && this.data.holdings.length > 0) {
      // Set full holdings data to sortableTableRows & define column definitions.
      this.sortedTableRows = this.data.holdings;
      // Fetch colDefs as per product type received.
      this.showMoneyfund =
        this.data.productType === ConfigurationId.MMF &&
        this.siteConfigService.isSiteInternational() &&
        this.data.isTopTenHoldingsMoneyFunds;
      if (this.showMoneyfund) {
        this.showPagination = false; // Hide pagination as there would be only 10 rows at most.
        this.columnDefs = this.getColumnDefsForMoneyFunds();
      } else {
        if (this.data.isTopHoldingData) {
          this.showPagination = false; // Hide pagination as there would be only 10 rows at most.
          this.columnDefs = this.getColumnDefsForTopHoldings();
        } else {
          this.columnDefs = this.getDefaultColumnDefsForHoldings();
        }
      }

      // If hideColumns present, fiter the same by hideColumn list and then assign hide property as true to them individually.
      if (this.data.hideColumns) {
        this.columnDefs
          .filter((colDef: ColDef) =>
            this.data.hideColumns?.includes(colDef.colId)
          )
          .forEach((colDef: ColDef) => (colDef.hide = true));
      }

      // Subscribe to total pages count and current page number when filter happens.
      this.dataSubscription.push(
        this.pagedRecords.numberOfPages$.subscribe((totalPages) => {
          this.totalPages = totalPages;
          this.paginationComponent?.updatePagination(totalPages);
        })
      );
      this.dataSubscription.push(
        this.pagedRecords.pageNumber$.subscribe((currentPageNumber) => {
          const grid = this.holdingsGrid;

          if (grid) {
            grid.api.paginationGoToPage(currentPageNumber - 1);

            if (!this.isProcessingFilter) {
              const firstRowIndex =
                this.holdingsGrid?.api.paginationGetPageSize() *
                (currentPageNumber - 1);

              this.holdingsGrid?.api.setFocusedCell(
                firstRowIndex,
                grid.columnApi
                  .getAllColumns()
                  .find((col) => col.isVisible())
                  .getColId()
              );
            }
          }
        })
      );
      this.dataSubscription.push(
        this.pagedRecords.currentPage$.subscribe(
          (currentPage) => (this.currentPage = currentPage)
        )
      );

      // Set total records to paged utils initially.
      // This will have total number of pages and current page number count to be initialized.
      this.pagedRecords.records = this.data.holdings;

      // Also, set holdings column names to search list.
      // This will be used in filter logic.
      this.holdingsColumnSearchList = Object.keys(this.data.holdings[0]);

      // Finally, set visibility of holding to be true, when everything is done.
      this.isPopulated = true;
    }
  }

  /**
   * Construct & return grid column definitions for top holdings.
   */
  getColumnDefsForTopHoldings(): ColDef[] {
    return [
      {
        colId: 'holdingName',
        headerName: this.translateService.instant(
          'pds.SECURITY_NAME',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:HoldingName`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-holding-name'
          ),
          analyticsName: this.translateService.instant(
            'pds.SECURITY_NAME',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopHoldingTable).holdingName;
        },
        autoHeight: true,
        wrapText: true,
        minWidth: 200,
        maxWidth: 350,
        flex: 2,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'geoCode',
        headerName: this.translateService.instant(
          'pds.GEOGRAPHY_CODE',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:GeoCode`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-geo-code'
          ),
          analyticsName: this.translateService.instant(
            'pds.GEOGRAPHY_CODE',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopHoldingTable).geoCode;
        },
        autoHeight: true,
        wrapText: true,
        minWidth: 200,
        maxWidth: 350,
        flex: 1,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'sectorName',
        headerName: this.translateService.instant(
          'pds.SECTOR_NAME',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SectorName`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-sector-name'
          ),
          analyticsName: this.translateService.instant(
            'pds.SECTOR_NAME',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopHoldingTable).sectorName;
        },
        autoHeight: true,
        wrapText: true,
        minWidth: 200,
        maxWidth: 350,
        flex: 2,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'breakdownPercentage',
        headerName: this.translateService.instant(
          'pds.PCT_OF_NET_ASSETS',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:BreakdownPercentage`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-breakdown-percentage'
          ),
          analyticsName: this.translateService.instant(
            'pds.PCT_OF_NET_ASSETS',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopHoldingTable).breakdownPercentage;
        },
        autoHeight: true,
        sortable: true,
        flex: 1,
        comparator: floatComparator,
        cellStyle: {
          whitespace: 'normal',
        },
      },
    ];
  }

  /**
   * Construct & return default grid column definitions.
   */
  getDefaultColumnDefsForHoldings(): ColDef[] {
    return [
      // WDE-3659: order updated
      {
        colId: 'securityName',
        headerName: this.translateService.instant('pds.SECURITY_NAME'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SecurityName`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-security-name'
          ),
          analyticsName: this.translateService.instant('pds.SECURITY_NAME'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).securityName;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'sectorName',
        headerName: this.translateService.instant('pds.SECTOR_NAME'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SectorName`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-sector-name'
          ),
          analyticsName: this.translateService.instant('pds.SECTOR_NAME'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).sectorName;
        },
        sortable: true,
        comparator: stringCompare,
        autoHeight: true,
        wrapText: true,
      },
      {
        colId: 'percentageOfNetAssets',
        headerName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:PercentageOfNetAssets`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-percentage-of-net-assets'
          ),
          analyticsName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).percentageOfNetAssets;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        sort: 'desc',
      },
      {
        colId: 'marketValue',
        headerName: this.translateService.instant('products.market-value'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:MarketValue`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-market-value'
          ),
          analyticsName: this.translateService.instant('products.market-value'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).marketValue;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
      },
      {
        colId: 'notionalMarketValue',
        headerName: this.translateService.instant('pds.NOTIONAL_MARKET_VALUE'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:NotionalMarketValue`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-notional-market-value'
          ),
          analyticsName: this.translateService.instant(
            'pds.NOTIONAL_MARKET_VALUE'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).notionalMarketValue;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
      },
      {
        colId: 'quantityShrpar',
        headerName: this.translateService.instant('pds.QUANTITY_SHRPAR'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:QuantityShrpar`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-quantity-shrpar'
          ),
          analyticsName: this.translateService.instant('pds.QUANTITY_SHRPAR'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).quantityShrpar;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'securityTicker',
        headerName: this.translateService.instant(
          'pds.SECURITY_TICKER',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SecurityTicker`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-security-ticker'
          ),
          analyticsName: this.translateService.instant(
            'pds.SECURITY_TICKER',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).securityTicker;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'isinSecurityNumber',
        headerName: this.translateService.instant(
          'pds.ISIN_SECURITY_NUMBER',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:IsinSecurityNumber`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-isin-security-number'
          ),
          analyticsName: this.translateService.instant(
            'pds.ISIN_SECURITY_NUMBER',
            this.brConfig?.fundId
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).isinSecurityNumber;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'originalCouponRate',
        headerName: this.translateService.instant('pds.ORIGINAL_COUPON_RATE'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:OriginalCouponRate`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-original-coupon-rate'
          ),
          analyticsName: this.translateService.instant(
            'pds.ORIGINAL_COUPON_RATE'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).originalCouponRate;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'securityExpirationDate',
        headerName: this.translateService.instant(
          'pds.SECURITY_EXPIRATION_DATE'
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SecurityExpirationDate`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-security-expiration-date'
          ),
          analyticsName: this.translateService.instant(
            'pds.SECURITY_EXPIRATION_DATE'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).securityExpirationDate;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
      },
      {
        colId: 'investmentCategory',
        headerName: this.translateService.instant('pds.INVESTMENT_CATEGORY'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:InvestmentCategory`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-investment-category'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).investmentCategory;
        },
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        colId: 'assetClassCategory',
        headerName: this.translateService.instant('pds.ASSET_CLASS_CATEGORY'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:AssetClassCategory`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-asset-class-category'
          ),
          analyticsName: this.translateService.instant(
            'pds.ASSET_CLASS_CATEGORY'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).assetClassCategory;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
      },
      {
        colId: 'marketCurrency',
        headerName: this.translateService.instant('pds.MARKET_CURRENCY'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:MarketCurrency`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-market-currency'
          ),
          analyticsName: this.translateService.instant('pds.MARKET_CURRENCY'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).marketCurrency;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
      },

      // Cols below weren't specified in updated WDE-3659 list
      {
        colId: 'cusipNumber',
        headerName: this.translateService.instant(
          'pds.CUSIP',
          this.brConfig?.fundId
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:CusipNumber`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-cusip-number'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).cusipNumber;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'finalMaturityDate',
        headerName: this.translateService.instant('pds.FINAL_MATURITY_DATE'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:FinalMaturityDate`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-final-maturity-date'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).finalMaturityDate;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
      },
      {
        colId: 'contracts',
        headerName: this.translateService.instant('pds.CONTRACTS'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:Contracts`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-contracts'
          ),
          analyticsName: this.translateService.instant('pds.CONTRACTS'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioHolding).contracts;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
      },
    ];
  }

  /**
   * Construct & return grid column definitions for money market funds.
   */
  getColumnDefsForMoneyFunds(): ColDef[] {
    return [
      {
        colId: 'securityName',
        headerName: this.translateService.instant('pds.SECURITY_NAME'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:SecurityName`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-security-name'
          ),
          analyticsName: this.translateService.instant('pds.SECURITY_NAME'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopTenHoldingMoneyFunds).securityName;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whitespace: 'normal',
        },
        flex: 1,
      },
      {
        colId: 'countryOfIssue',
        headerName: this.translateService.instant('pds.GEOGRAPHY_CODE'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:CountryOfIssue`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-country-of-issue'
          ),
          analyticsName: this.translateService.instant('pds.GEOGRAPHY_CODE'),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopTenHoldingMoneyFunds)
            .countryOfIssue;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'holdingValue',
        headerName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:HoldingValue`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-holding-value'
          ),
          analyticsName: this.translateService.instant('pds.PCT_OF_NET_ASSETS'),
        },
        valueGetter: (params) => {
          return formatPercentage(
            (params.data as PortfolioTopTenHoldingMoneyFunds).holdingValue
          );
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'maturityDate',
        headerName: this.translateService.instant(
          'pds.SECURITY_EXPIRATION_DATE'
        ),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:MaturityDate`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-maturity-date'
          ),
          analyticsName: this.translateService.instant(
            'pds.SECURITY_EXPIRATION_DATE'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopTenHoldingMoneyFunds).maturityDate;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
      {
        colId: 'assetType',
        headerName: this.translateService.instant('pds.ASSET_CLASS_CATEGORY'),
        headerComponentFramework: HeaderRendererComponent,
        headerComponentParams: {
          footnotePlacement: `Holdings:AssetType`,
          tooltipContent: this.translateService.tooltip(
            'products.portfolio-holdings-asset-type'
          ),
          analyticsName: this.translateService.instant(
            'pds.ASSET_CLASS_CATEGORY'
          ),
        },
        valueGetter: (params) => {
          return (params.data as PortfolioTopTenHoldingMoneyFunds).assetType;
        },
        sortable: true,
        comparator: floatComparator,
        autoHeight: true,
        wrapText: true,
        cellStyle: {
          whitespace: 'normal',
        },
      },
    ];
  }

  /**
   * Return info item object for a particular holding & column definition.
   * @param holding holding object
   * @param colDef column definition
   */
  getInfoItem(
    holding: PortfolioHolding | PortfolioTopTenHoldingMoneyFunds,
    colDef: ColDef
  ): InfoItem {
    let val = '';

    if (colDef.valueGetter) {
      if (typeof colDef.valueGetter === 'string') {
        val = colDef.valueGetter;
      } else {
        val = colDef.valueGetter({
          data: holding,
        } as ValueGetterParams);
      }
    }

    return {
      label: colDef.headerName,
      val,
    };
  }

  /**
   * *** Set external filter ag-grid configurations ***
   */

  isExternalFilterPresent = (): boolean => true;

  doesExternalFilterPass(node: any): boolean {
    if (node.gridOptionsWrapper.getContext().filteredRows) {
      return (
        node.gridOptionsWrapper.getContext().filteredRows.indexOf(node.data) >
        -1
      );
    }
    return true;
  }

  /**
   * Change detection on sorting
   */
  onSortChanged = () => this.cdr.detectChanges();

  onFirstDataRendered = () => {
    this.holdingsGrid?.api.resetRowHeights();
  };

  /**
   * Trigger page number selection accordingly.
   * @param event page changed event generated
   */
  goToPage(event: EDSPaginationPageChangedEvent): void {
    if (this.pagedRecords && event.action !== 'load') {
      this.pagedRecords.pageNumber = event.page;

      this.analytics.trackEvent({
        event: ANALTICS_PAGINATION_EVENT,
        name: this.titleService.getTitle(),
        action:
          event.action === PaginationActionEnum.activePage
            ? event.page.toString()
            : event.action,
      });
    }
  }

  filterOnDate(selectedItem) {
    let filteredHoldings: PortfolioHolding[] = [];

    filteredHoldings = (this.data.holdings as any[]).filter((column) => {
      return column.asOfDateStd === selectedItem.value;
    });

    this.holdingsColumnFilteredList = filteredHoldings;
    this.sortedTableRows = this.holdingsColumnFilteredList;
    this.pagedRecords.records = this.holdingsColumnFilteredList;
    this.pagedRecords.pageNumber = 1;

    this.selectedFilteredDate = selectedItem.label;
  }
  /**
   * Filter holdings table based upon input text.
   * @param value filter text
   */
  filterHoldings(value: string): void {
    this.isProcessingFilter = true;

    const filteredHoldings: PortfolioHolding[] = [];
    if (value.trim() !== '') {
      // Intentionally used 'any[]' here.
      // As filter isn't able to handle 'PortfolioHolding' | 'PortfolioTopTenHoldingMoneyFunds' simultaneously.
      // This is due to signature of both types being different from each other.
      // But, the same is compensated inside filter as 'holding' object, to be specific to above types only.

      if (this.data?.filteredAsOfDate?.length > 1) {
        (this.holdingsColumnFilteredList as any[]).forEach(
          (holding: PortfolioHolding | PortfolioTopTenHoldingMoneyFunds) => {
            const searchedColumn = this.holdingsColumnSearchList.find(
              (column) =>
                holding[column]?.toLowerCase().includes(value.toLowerCase())
            );

            if (searchedColumn) {
              filteredHoldings.push(holding);
            }
          }
        );
      } else {
        (this.data.holdings as any[]).forEach(
          (holding: PortfolioHolding | PortfolioTopTenHoldingMoneyFunds) => {
            const searchedColumn = this.holdingsColumnSearchList.find(
              (column) =>
                holding[column]?.toLowerCase().includes(value.toLowerCase())
            );

            if (searchedColumn) {
              filteredHoldings.push(holding);
            }
          }
        );
      }

      this.sortedTableRows = filteredHoldings;
      this.pagedRecords.records = filteredHoldings;
      this.pagedRecords.pageNumber = 1;
    } else if (this.data?.filteredAsOfDate?.length > 1) {
      this.sortedTableRows = this.holdingsColumnFilteredList;
      this.pagedRecords.records = this.holdingsColumnFilteredList;
      this.pagedRecords.pageNumber = 1;
    } else {
      this.sortedTableRows = this.data.holdings;
      this.pagedRecords.records = this.data.holdings;
      this.pagedRecords.pageNumber = 1;
    }
    this.isProcessingFilter = false;
  }

  /**
   * Parses params and converts them to event object
   */
  createEventParams(params: PortfolioHoldingData): void {
    this.eventParams = {
      location: '',
      file_name: params?.fundName,
      file_extension: 'xlsx',
    };
  }

  /**
   * Handle download action.
   * Convert table records into excel format and initiate download.
   */
  triggerDownloadAction(): void {
    const mappedBody:
      | PortfolioDownloadableTableRowData[]
      | PortfolioMoneyFundDownloadableTableRowData[] = this.mapDownloadData(
      this.getTotalFilteredRecords()
    );
    const mappedHeader = new Map<string, string>();
    if (this.showMoneyfund) {
      mappedHeader.set('securityName', 'pds.SECURITY_NAME');
      mappedHeader.set('countryOfIssue', 'pds.GEOGRAPHY_CODE');
      mappedHeader.set('holdingValue', 'pds.PCT_OF_NET_ASSETS');
      mappedHeader.set('maturityDate', 'pds.SECURITY_EXPIRATION_DATE');
      mappedHeader.set('assetType', 'pds.ASSET_CLASS_CATEGORY');
    } else {
      if (!this.data.isTopHoldingData) {
        mappedHeader.set('securityTicker', 'pds.SECURITY_TICKER');
      }
      mappedHeader.set('isinSecurityNumber', 'pds.ISIN_SECURITY_NUMBER');
      mappedHeader.set('cusipNumber', 'pds.CUSIP');
      mappedHeader.set('securityName', 'pds.SECURITY_NAME');
      mappedHeader.set('quantityShrpar', 'pds.QUANTITY_SHRPAR');
      mappedHeader.set('originalCouponRate', 'pds.ORIGINAL_COUPON_RATE');
      if (!this.data.isTopHoldingData) {
        mappedHeader.set('sectorName', 'pds.SECTOR_NAME');
      }
      mappedHeader.set('percentageOfNetAssets', 'pds.PCT_OF_NET_ASSETS');
      mappedHeader.set('marketValue', 'products.market-value');
      mappedHeader.set('notionalMarketValue', 'pds.NOTIONAL_MARKET_VALUE');
      mappedHeader.set(
        'securityExpirationDate',
        'pds.SECURITY_EXPIRATION_DATE'
      );
      mappedHeader.set('finalMaturityDate', 'pds.FINAL_MATURITY_DATE');
      mappedHeader.set('investmentCategory', 'pds.INVESTMENT_CATEGORY');
      mappedHeader.set('assetClassCategory', 'pds.ASSET_CLASS_CATEGORY');
      mappedHeader.set('marketCurrency', 'pds.MARKET_CURRENCY');
      mappedHeader.set('contracts', 'pds.CONTRACTS');
    }
    mappedHeader.forEach((value, key) => {
      if (this.data.hideColumns?.includes(key)) {
        mappedHeader.delete(key);
      }
    });

    const mappedFullRecords = [
      Object.keys(mappedBody[0]).map((data) =>
        this.translateService.instant(mappedHeader.get(data))
      ),
    ];

    mappedBody.forEach((data) => {
      mappedFullRecords.push(Object.values(data));
    });

    const fundName = [];

    fundName.push([
      this.data.fundName,
      this.translateService.instant('products.as-of'),
      this.selectedFilteredDate,
    ]);

    forkJoin([
      this.getProximalCaveat('FullHoldingsExcelBottom'),
      this.getProximalCaveat('FullHoldingsExcelTop'),
    ]).subscribe((data) => {
      const [bottomCaveat, topCaveat]: [string[][], string[][]] = data;
      this.exportToExcel(mappedFullRecords, fundName, topCaveat, bottomCaveat);
    });
    this.analytics.trackEvent({
      event: this.eventId,
      ...this.eventParams,
    });
  }

  /**
   * get the proximal caveats for a particular placement
   */
  getProximalCaveat(placement: string): Observable<string[][]> {
    return this.caveatService.getProximals$(placement).pipe(
      take(1),
      map((caveatContext) => {
        const caveat = [];
        const caveatData = caveatContext.map((data) => data.split('\n'));

        caveatData[0]?.map((data: any) => {
          let trimedData = data.replace(/(<([^>]+)>)/gi, '');
          trimedData = this.replaceWithAmpersand(trimedData);

          if (trimedData.length > 0) {
            caveat.push([trimedData]);
          }
        });

        return caveat;
      }),
      catchError(() => {
        return [];
      })
    );
  }

  replaceWithAmpersand(data: string): string {
    const regex = /&amp;/gi;
    return data.replace(regex, '&');
  }

  /**
   * Return mapped data as per downloadable action.
   * @param totalRecords total filtered records
   */
  mapDownloadData(
    totalRecords: PortfolioHolding[] | PortfolioTopTenHoldingMoneyFunds[]
  ):
    | PortfolioDownloadableTableRowData[]
    | PortfolioMoneyFundDownloadableTableRowData[] {
    const mappedData = [];

    totalRecords.forEach((record) => {
      let mappedRecord = {};
      if (this.showMoneyfund) {
        mappedRecord = {
          securityName: record.securityName,
          countryOfIssue: record.countryOfIssue,
          holdingValue: record.holdingValue,
          maturityDate: record.maturityDate,
          assetType: record.assetType,
        };
      } else {
        if (this.data.isTopHoldingData) {
          mappedRecord = {
            isinSecurityNumber: record.isinSecurityNumber,
            securityName: record.securityName,
            quantityShrpar: record.quantityShrpar,
            originalCouponRate: record.originalCouponRate,
            percentageOfNetAssets: record.percentageOfNetAssets,
            marketValue: record.marketValue,
            notionalMarketValue: record.notionalMarketValue,
            securityExpirationDate: record.securityExpirationDate,
            assetClassCategory: record.assetClassCategory,
            marketCurrency: record.marketCurrency,
            contracts: record.contracts,
          };
        } else {
          mappedRecord = {
            securityTicker: record.securityTicker,
            isinSecurityNumber: record.isinSecurityNumber,
            cusipNumber: record.cusipNumber,
            securityName: record.securityName,
            quantityShrpar: record.quantityShrpar,
            originalCouponRate: record.originalCouponRate,
            sectorName: record.sectorName,
            percentageOfNetAssets: record.percentageOfNetAssets,
            marketValue: record.marketValue,
            notionalMarketValue: record.notionalMarketValue,
            securityExpirationDate: record.securityExpirationDate,
            finalMaturityDate: record.finalMaturityDate,
            investmentCategory: record.investmentCategory,
            assetClassCategory: record.assetClassCategory,
            marketCurrency: record.marketCurrency,
            contracts: record.contracts,
          };
        }
      }
      Object.keys(mappedRecord).forEach((key) => {
        if (this.data.hideColumns?.includes(key)) {
          delete mappedRecord[key];
        }
      });
      mappedData.push(mappedRecord);
    });

    return mappedData;
  }

  /**
   * Export the table data to excel and auto download.
   * @param data full record for excel
   */
  exportToExcel(
    data: string[][],
    fundName: string[][],
    topCaveat: string[][],
    bottomCaveat: string[][]
  ): void {
    import('xlsx').then((xlsx) => {
      const workSheet = xlsx.utils.aoa_to_sheet(topCaveat);

      const workBook = xlsx.utils.book_new();

      workSheet['!cols'] = this.fitToColumn(fundName);

      xlsx.utils.sheet_add_aoa(workSheet, fundName, {
        origin: 'A' + (topCaveat.length + 2),
      });

      xlsx.utils.sheet_add_aoa(workSheet, data, {
        origin: 'A' + (topCaveat.length + fundName.length + 3),
      });

      xlsx.utils.sheet_add_aoa(workSheet, bottomCaveat, {
        origin: 'A' + (data.length + 8 + topCaveat.length + fundName.length),
      });

      const portfolioHoldingTabname = this.translateService.instant(
        'products.portfolio-allholding-tabname'
      );
      const portfolioHoldingFilename = this.translateService.instant(
        'products.portfolio-allholding-filename'
      );

      xlsx.utils.book_append_sheet(
        workBook,
        workSheet,
        portfolioHoldingTabname
      );
      xlsx.writeFile(workBook, portfolioHoldingFilename);
    });
  }

  /**
   * Get maximum character of each column.
   * @param data full record for excel
   */
  fitToColumn(data: string[][]) {
    return data[0].map((a, i) => ({
      wch: Math.max(...data.map((a2) => a2[i].toString().length)),
    }));
  }

  /**
   * Return total filtered records.
   */
  getTotalFilteredRecords():
    | PortfolioHolding[]
    | PortfolioTopTenHoldingMoneyFunds[] {
    return this.pagedRecords.records;
  }

  /**
   * Remove the subscribed observables on page destroy.
   */
  ngOnDestroy(): void {
    this.agGridService.removeGrid(this.holdingsGrid);
    this.dataSubscription.forEach((subscription) => subscription.unsubscribe());
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
